import React, { createContext, useContext, useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'https://financial-analyst-backend-production-7175.up.railway.app/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch user data from token
  const fetchUserFromToken = async (authToken) => {
    try {
      const response = await fetch(`${API_URL}/auth/verify-token`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      
      const data = await response.json();
      
      if (data.success && data.user) {
        setUser(data.user);
        setToken(authToken);
        localStorage.setItem('investorq_token', authToken);
        localStorage.setItem('investorq_user', JSON.stringify(data.user));
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error fetching user from token:', error);
      return false;
    }
  };

  // Load user from localStorage on mount + handle OAuth redirect
  useEffect(() => {
    const initAuth = async () => {
      // Check for OAuth redirect token in URL
      const urlParams = new URLSearchParams(window.location.search);
      const oauthToken = urlParams.get('token');
      const authStatus = urlParams.get('auth');
      const oauthError = urlParams.get('error');

      // Handle OAuth error
      if (oauthError) {
        console.error('OAuth error:', oauthError);
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);
        setLoading(false);
        return;
      }

      // Handle successful OAuth redirect
      if (oauthToken && authStatus === 'success') {
        console.log('OAuth token received, fetching user...');
        const success = await fetchUserFromToken(oauthToken);
        
        // Clean up URL (remove token from address bar for security)
        window.history.replaceState({}, document.title, window.location.pathname);
        
        if (success) {
          console.log('✅ OAuth login successful');
          setLoading(false);
          return;
        }
      }

      // Load from localStorage if no OAuth redirect
      const storedToken = localStorage.getItem('investorq_token');
      const storedUser = localStorage.getItem('investorq_user');
      
      if (storedToken && storedUser) {
        setToken(storedToken);
        setUser(JSON.parse(storedUser));
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email, password) => {
    try {
      const response = await fetch(`${API_URL}/users/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (!response.ok) {
        // Check if OTP is required
        if (data.requiresOTP) {
          return {
            success: false,
            requiresOTP: true,
            userId: data.userId,
            email: data.email
          };
        }
        
        // Check if account is deactivated
        if (response.status === 403 && data.isDeactivated) {
          return {
            success: false,
            error: data.error,
            isDeactivated: true,
            email: email,
            password: password
          };
        }
        return { success: false, error: data.error || 'Login failed' };
      }

      // Save to state and localStorage
      setUser(data.user);
      setToken(data.token);
      localStorage.setItem('investorq_token', data.token);
      localStorage.setItem('investorq_user', JSON.stringify(data.user));

      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  const register = async (email, password, name) => {
    try {
      const response = await fetch(`${API_URL}/users/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password, name })
      });

      const data = await response.json();

      if (!response.ok) {
        // Check if account can be reactivated
        if (data.canReactivate) {
          return {
            success: false,
            error: data.error,
            canReactivate: true
          };
        }
        return { success: false, error: data.error || 'Registration failed' };
      }

      // Save to state and localStorage
      setUser(data.user);
      setToken(data.token);
      localStorage.setItem('investorq_token', data.token);
      localStorage.setItem('investorq_user', JSON.stringify(data.user));

      return { success: true };
    } catch (error) {
      console.error('Registration error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  const reactivateAccount = async (email, password) => {
    try {
      const response = await fetch(`${API_URL}/users/reactivate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, error: data.error || 'Reactivation failed' };
      }

      // Save to state and localStorage
      setUser(data.user);
      setToken(data.token);
      localStorage.setItem('investorq_token', data.token);
      localStorage.setItem('investorq_user', JSON.stringify(data.user));

      return { success: true, message: data.message };
    } catch (error) {
      console.error('Reactivation error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  // Complete OTP verification and login
  const completeOTPLogin = async (otpData) => {
    if (otpData.success && otpData.token && otpData.user) {
      setUser(otpData.user);
      setToken(otpData.token);
      localStorage.setItem('investorq_token', otpData.token);
      localStorage.setItem('investorq_user', JSON.stringify(otpData.user));
      return { success: true };
    }
    return { success: false, error: 'Invalid OTP response' };
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('investorq_token');
    localStorage.removeItem('investorq_user');
  };

  // Refresh user data (useful after subscription changes)
  const refreshUser = async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/auth/verify-token`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (data.success && data.user) {
        setUser(data.user);
        localStorage.setItem('investorq_user', JSON.stringify(data.user));
      }
    } catch (error) {
      console.error('Error refreshing user:', error);
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    reactivateAccount,
    refreshUser,
    completeOTPLogin,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;